/**
 * 25 Oct 2026 (D-1025-TODAY) — ONE rule for "visits to close out", shared by Housekeeping's Day (src/pages/Today.tsx,
 * its "visits to close out" group and its Needs your attention count) and the house Today (api/src/house-today), which
 * disagreed before: the Day counted every visit already started and not closed, the house Today only those an hour late.
 *
 * The Day's rule (kept exactly as it was in Today.tsx): a placed visit that is open (Scheduled or In Progress), has no
 * close-out waiting for the scheduler, is not locked from the person looking, whose planned start has passed — and which
 * is not the visit happening right now unless it is late. The Day's total is those visits plus the server's own
 * "Pending close-out" items it does not already list (a visit of an earlier day, for one).
 */
import { awaitsCloseOut, type PendingPress } from './with-scheduler';

const FINISH_WAITING: PendingPress = { queue: 'q1', kind: 'finish', operation: '', transactionId: '', at: '' };

export interface DayCloseoutFacts {
  status: string;
  /** A close-out waits for the scheduler (the visit's finish press is open). */
  closing: boolean;
  /** The crew member looking is locked from this visit. */
  locked: boolean;
  /** The day plan's start for the visit's position (minutes after midnight), or null when it has none. */
  startMinutes: number | null;
  /** The visit happening now (the card on top of the Day). */
  isNow: boolean;
  /** Past its end by the close-out reminder's minutes. */
  late: boolean;
}

/** The Day's rule for one placed visit. */
export function dayAwaitsCloseout(v: DayCloseoutFacts, nowMinutes: number): boolean {
  if (v.locked || !awaitsCloseOut(v, v.closing ? [FINISH_WAITING] : [])) return false;
  if (v.startMinutes === null || v.startMinutes > nowMinutes) return false;
  return !v.isNow || v.late;
}

/** A server "Pending close-out" item's appointment (the appointment, else its `CLOSEOUT-<id>` item id). */
export const closeoutItemAppointmentId = (i: { id: string; appointment?: { appointmentId: string } | null }): string => i.appointment?.appointmentId ?? i.id.replace(/^CLOSEOUT-/, '');

/** The Day's total: the visits it lists, plus the server's Housekeeping items it does not already list. */
export function closeoutTotal(listed: Iterable<string>, serverItems: Iterable<string>): number {
  const ids = new Set(listed);
  for (const id of serverItems) ids.add(id);
  return ids.size;
}
